import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Check, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";

export default function LessonView() {
  const { courseId, lessonId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [marked, setMarked] = useState(false);

  useEffect(() => {
    api.get(`/courses/${courseId}/lessons/${lessonId}`).then((r) => setData(r.data)).catch(() => {});
    if (user) {
      api.get(`/progress/${courseId}`).then((r) => setMarked(r.data.completed_lesson_ids.includes(lessonId))).catch(() => {});
    }
  }, [courseId, lessonId, user]);

  const markComplete = async () => {
    if (!user) { toast.error("Please sign in to track progress"); return; }
    await api.post("/progress", { course_id: courseId, lesson_id: lessonId });
    setMarked(true);
    toast.success("Lesson marked complete");
  };

  if (!data) return <div className="min-h-screen bg-[#0D0C0A] pt-32 px-6 text-[#B3AFA8]">Loading…</div>;

  const { course, lesson } = data;

  return (
    <main data-testid="lesson-view" className="bg-[#0D0C0A] min-h-screen pt-32 pb-24 px-6 md:px-12">
      <article className="max-w-3xl mx-auto">
        <Link
          to={`/classes/${course.id}`}
          data-testid="back-to-course"
          className="inline-flex items-center gap-2 text-[#B3AFA8] hover:text-[#D4AF37] text-sm mb-12 transition"
        >
          <ArrowLeft size={14} /> Back to {course.title}
        </Link>

        <p className="label-eyebrow mb-4">{course.level} · {course.title}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">{lesson.title}</h1>
        <p className="text-[#D4AF37] italic text-xl mb-14 border-l-2 border-[#D4AF37] pl-5">{lesson.summary}</p>

        <div className="lesson-prose text-lg">
          {lesson.content.split("\n\n").map((p, i) => <p key={i}>{p.split("\n").map((ln, j) => <span key={j}>{ln}{j < p.split("\n").length - 1 && <br />}</span>)}</p>)}
        </div>

        <div className="mt-20 pt-10 border-t border-[#332D29] flex flex-wrap gap-4 items-center justify-between">
          {marked ? (
            <span data-testid="completed-badge" className="inline-flex items-center gap-2 px-6 py-3 border border-[#D4AF37]/50 text-[#D4AF37] rounded-full text-sm">
              <Check size={16} /> Lesson complete
            </span>
          ) : (
            <button onClick={markComplete} data-testid="mark-complete-btn" className="btn-gold px-8 py-3 rounded-full text-sm inline-flex items-center gap-2">
              <Check size={16} /> Mark complete
            </button>
          )}
          <button onClick={() => nav("/chat")} data-testid="ask-about-lesson" className="btn-outline-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2">
            <MessageCircleQuestion size={16} /> Ask Marilyn about this
          </button>
        </div>
      </article>
    </main>
  );
}
